import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { fetchWithAuth } from "@/hooks/useAuth";
import { ProductVariant } from "@/prisma-types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Save, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";

type InventoryPolicy = "DENY" | "CONTINUE";

function coerceNum(n?: number | string | null): number | undefined {
  if (n === null || n === undefined || n === "") return undefined;
  const v = typeof n === "string" ? Number(n) : n;
  return Number.isFinite(v) ? (v as number) : undefined;
}

export default function ProductVariantsPannel({
  productId,
}: {
  productId: number;
}) {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);
  const [form, setForm] = React.useState({
    title: "",
    sku: "",
    barcode: "",
    price: "",
    compareAtPrice: "",
    costPrice: "",
    inventoryQuantity: "0",
    inventoryPolicy: "DENY" as InventoryPolicy,
    inventoryTracked: true,
    requiresShipping: true,
    weight: "",
    weightUnit: "",
  });

  type VariantListItem = ProductVariant;
  const { data: variants, isLoading } = useQuery<VariantListItem[], Error>({
    queryKey: ["product-variants", productId],
    queryFn: async () => {
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/variants`
      );
      if (!res.ok) throw new Error("Eroare la încărcarea variantelor");
      const json = await res.json();
      // Accept multiple API shapes: array, { data }, or { variants }
      if (Array.isArray(json)) return json as VariantListItem[];
      if (json && Array.isArray(json.date))
        return json.date as VariantListItem[];
      if (json && Array.isArray(json.variants))
        return json.variants as VariantListItem[];
      return [] as VariantListItem[];
    },
  });

  console.log("variants", variants);

  const createVariant = useMutation({
    mutationFn: async () => {
      const payload = {
        title: form.title.trim(),
        sku: form.sku || undefined,
        barcode: form.barcode || undefined,
        price: Number(form.price),
        compareAtPrice: coerceNum(form.compareAtPrice),
        costPrice: coerceNum(form.costPrice),
        inventoryQuantity: Number(form.inventoryQuantity || 0),
        inventoryPolicy: form.inventoryPolicy,
        requiresShipping: form.requiresShipping,
        inventoryTracked: form.inventoryTracked,
        weight: coerceNum(form.weight),
        weightUnit: form.weightUnit || undefined,
      };
      const res = await fetchWithAuth(
        `/api/admin/products/${productId}/variants`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || "Eroare la crearea variantei");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product-variants", productId],
      });
      setIsCreating(false);
      setForm({
        title: "",
        sku: "",
        barcode: "",
        price: "",
        compareAtPrice: "",
        costPrice: "",
        inventoryQuantity: "0",
        inventoryPolicy: "DENY",
        inventoryTracked: true,
        requiresShipping: true,
        weight: "",
        weightUnit: "",
      });
    },
  });

  const updateVariant = useMutation({
    mutationFn: async (v: Partial<ProductVariant> & { id: number }) => {
      const res = await fetchWithAuth(`/api/admin/products/variants/${v.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(v.title !== undefined ? { title: v.title } : {}),
          ...(v.sku !== undefined ? { sku: v.sku } : {}),
          ...(v.barcode !== undefined ? { barcode: v.barcode } : {}),
          ...(v.price !== undefined ? { price: v.price } : {}),
          ...(v.compareAtPrice !== undefined
            ? { compareAtPrice: v.compareAtPrice }
            : {}),
          ...(v.costPrice !== undefined ? { costPrice: v.costPrice } : {}),
          ...(v.inventoryQuantity !== undefined
            ? { inventoryQuantity: v.inventoryQuantity }
            : {}),
          ...(v.inventoryPolicy !== undefined
            ? { inventoryPolicy: v.inventoryPolicy }
            : {}),
          ...(v.requiresShipping !== undefined
            ? { requiresShipping: v.requiresShipping }
            : {}),
          ...(v.inventoryTracked !== undefined
            ? { inventoryTracked: v.inventoryTracked }
            : {}),
          ...(v.weight !== undefined ? { weight: v.weight } : {}),
          ...(v.weightUnit !== undefined ? { weightUnit: v.weightUnit } : {}),
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || "Eroare la actualizarea variantei");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["product-variants", productId],
      });
    },
  });

  const deleteVariant = useMutation({
    mutationFn: async (variantId: number) => {
      const res = await fetchWithAuth(
        `/api/admin/products/variants/${variantId}`,
        {
          method: "DELETE",
        }
      );
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.message || "Eroare la ștergerea variantei");
      }
    },
    onMutate: (variantId: number) => {
      setDeletingId(variantId);
    },
    onSuccess: () => {
      toast.success("Variantă ștearsă");
      queryClient.invalidateQueries({
        queryKey: ["product-variants", productId],
      });
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Eroare la ștergere"
      );
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  return (
    <Card className="">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">Variante</div>
          <Button size="sm" onClick={() => setIsCreating((s) => !s)}>
            <Plus className="h-4 w-4 mr-2" /> Adaugă varianta
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isCreating && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createVariant.mutate();
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-3 border rounded p-3"
          >
            <Input
              placeholder="Titlu"
              value={form.title}
              onChange={(e) =>
                setForm((f) => ({ ...f, title: e.target.value }))
              }
              required
            />
            <Input
              placeholder="SKU (cod produs SmartBill)"
              value={form.sku}
              onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
            />
            <Input
              placeholder="Barcode"
              value={form.barcode}
              onChange={(e) =>
                setForm((f) => ({ ...f, barcode: e.target.value }))
              }
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Preț"
              value={form.price}
              onChange={(e) =>
                setForm((f) => ({ ...f, price: e.target.value }))
              }
              required
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Preț comparativ"
              value={form.compareAtPrice}
              onChange={(e) =>
                setForm((f) => ({ ...f, compareAtPrice: e.target.value }))
              }
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Cost"
              value={form.costPrice}
              onChange={(e) =>
                setForm((f) => ({ ...f, costPrice: e.target.value }))
              }
            />
            <Input
              type="number"
              placeholder="Stoc"
              value={form.inventoryQuantity}
              onChange={(e) =>
                setForm((f) => ({ ...f, inventoryQuantity: e.target.value }))
              }
            />
            <Input
              placeholder="Politică inventar (DENY/CONTINUE)"
              value={form.inventoryPolicy}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  inventoryPolicy: e.target.value as InventoryPolicy,
                }))
              }
            />
            <div className="flex items-center gap-2">
              <label className="text-sm">Urmărește inventar</label>
              <input
                type="checkbox"
                checked={form.inventoryTracked}
                onChange={(e) =>
                  setForm((f) => ({ ...f, inventoryTracked: e.target.checked }))
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm">Necesită livrare</label>
              <input
                type="checkbox"
                checked={form.requiresShipping}
                onChange={(e) =>
                  setForm((f) => ({ ...f, requiresShipping: e.target.checked }))
                }
              />
            </div>
            <Input
              type="number"
              step="0.01"
              placeholder="Greutate"
              value={form.weight}
              onChange={(e) =>
                setForm((f) => ({ ...f, weight: e.target.value }))
              }
            />
            <Input
              placeholder="Unitate greutate (ex: kg)"
              value={form.weightUnit}
              onChange={(e) =>
                setForm((f) => ({ ...f, weightUnit: e.target.value }))
              }
            />
            <div className="col-span-full flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreating(false)}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={createVariant.isPending}>
                {createVariant.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Salvare...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" /> Salvează
                  </>
                )}
              </Button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Titlu</TableHead>
                <TableHead>SKU (cod produs SmartBill)</TableHead>
                <TableHead>Preț neredus (taxe incluse)</TableHead>
                <TableHead>Preț total (taxe incluse)</TableHead>
                <TableHead>Stoc</TableHead>
                <TableHead>Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6">
                    Se încarcă...
                  </TableCell>
                </TableRow>
              ) : (variants ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-6">
                    Nu există variante
                  </TableCell>
                </TableRow>
              ) : (
                (variants ?? []).map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>#{v.id}</TableCell>
                    <TableCell>
                      <Input
                        defaultValue={v.title}
                        onBlur={(e) =>
                          updateVariant.mutate({
                            id: v.id,
                            title: e.target.value,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        defaultValue={v.sku ?? ""}
                        onBlur={(e) =>
                          updateVariant.mutate({
                            id: v.id,
                            sku: e.target.value || null,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={
                          v.compareAtPrice !== null &&
                          v.compareAtPrice !== undefined
                            ? String(v.compareAtPrice)
                            : ""
                        }
                        onBlur={(e) =>
                          updateVariant.mutate({
                            id: v.id,
                            compareAtPrice:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        defaultValue={String(v.price)}
                        onBlur={(e) =>
                          updateVariant.mutate({
                            id: v.id,
                            price: Number(e.target.value || 0),
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        defaultValue={String(v.inventoryQuantity)}
                        onBlur={(e) =>
                          updateVariant.mutate({
                            id: v.id,
                            inventoryQuantity: Number(e.target.value || 0),
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteVariant.mutate(v.id)}
                          disabled={
                            deletingId === v.id && deleteVariant.isPending
                          }
                        >
                          {deletingId === v.id && deleteVariant.isPending ? (
                            <>
                              <Spinner className="mr-2" /> Ștergere...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" /> Șterge
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
